import axios from "axios";
import { enqueue, cacheRead, loadRead, flushQueue } from "./offline";
import { toast } from "sonner";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = BASE;
export const API = `${BASE}/api`;

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("nicu_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

function isWrite(m) { return ["post", "put", "patch", "delete"].includes((m || "").toLowerCase()); }
function urlKey(cfg) {
  const params = cfg.params ? "?" + new URLSearchParams(cfg.params).toString() : "";
  return (cfg.baseURL || "") + (cfg.url || "") + params;
}

api.interceptors.response.use(
  (resp) => {
    // Cache GET responses
    try {
      if ((resp.config.method || "").toLowerCase() === "get") {
        cacheRead(urlKey(resp.config), resp.data);
      }
    } catch (e) { console.warn("Read cache write failed", e); }
    return resp;
  },
  async (err) => {
    if (err?.response?.status === 401) {
      localStorage.removeItem("nicu_token");
      localStorage.removeItem("nicu_user");
      if (window.location.pathname !== "/login") window.location.href = "/login";
      return Promise.reject(err);
    }

    const isNetworkErr = !err.response || err.code === "ERR_NETWORK";
    if (!isNetworkErr) return Promise.reject(err);

    const cfg = err.config || {};
    const method = (cfg.method || "get").toLowerCase();

    // GET fallback → serve from localStorage cache
    if (method === "get") {
      const cached = loadRead(urlKey(cfg));
      if (cached) {
        return { data: cached.data, status: 200, headers: {}, config: cfg, __fromCache: true };
      }
      return Promise.reject(err);
    }

    // Write fallback → enqueue and return optimistic response
    if (isWrite(method)) {
      // Files can't be safely serialised to IndexedDB in FormData form; skip queueing for multipart
      const hasFile = cfg.headers?.["Content-Type"]?.toString().includes("multipart");
      if (hasFile) {
        toast.error("Uploads require internet — try again when online");
        return Promise.reject(err);
      }
      const clientId = "offline_" + Math.random().toString(36).slice(2, 10);
      await enqueue({
        method,
        url: cfg.url,
        data: cfg.data ? (typeof cfg.data === "string" ? JSON.parse(cfg.data) : cfg.data) : undefined,
        headers: cfg.headers && cfg.headers.Authorization ? { Authorization: cfg.headers.Authorization } : {},
        params: cfg.params,
      });
      // Register a background sync tag so the browser flushes when connectivity returns
      // even if the tab is in the background.
      try {
        if ("serviceWorker" in navigator && "SyncManager" in window) {
          const reg = await navigator.serviceWorker.ready;
          await reg.sync.register("nicu-flush");
        }
      } catch (e) { console.warn("Background sync registration failed", e); }
      const body = typeof cfg.data === "string" ? JSON.parse(cfg.data) : cfg.data || {};
      toast.info("Saved offline — will sync when online");
      return { data: { id: clientId, _queued: true, ...body }, status: 202, config: cfg, headers: {}, __queued: true };
    }

    return Promise.reject(err);
  }
);

// Wire online → flush
if (typeof window !== "undefined") {
  const doFlush = async () => {
    try {
      const res = await flushQueue(api);
      if (res.ok > 0) toast.success(`Synced ${res.ok} offline change${res.ok === 1 ? "" : "s"}`);
    } catch (e) { console.warn("Offline queue flush failed", e); }
  };
  window.addEventListener("online", doFlush);
  // Retry every 60s in case of intermittent connectivity
  setInterval(doFlush, 60_000);
  // Attempt one flush on load if we're online
  if (navigator.onLine) setTimeout(doFlush, 3000);
  // Service worker → page: FLUSH_QUEUE message (from background sync)
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener("message", (e) => {
      if (e.data && e.data.type === "FLUSH_QUEUE") doFlush();
    });
  }
}

export function authFileURL(fileId) {
  const t = localStorage.getItem("nicu_token") || "";
  return `${API}/files/${fileId}/download?auth=${encodeURIComponent(t)}`;
}
